import { useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { UserContext, UserContextPayload } from '../Context/UserDetailContext';
import GlobalLoader from '../GlobalComponent/GlobalLoader/GlobalLoader';
import Signin from '../Pages/Signin/Signin';
import SuperAdminDashboard from '../Pages/Dashboard/superAdminDashboard';
import MainComponent from '../Pages/MainComponent/MainComponent';
import Profile from '../Pages/Profile/Profile';
import Training from '../Pages/Training/Training';
import Agent from '../Pages/Agent/Agent';
import LandingPageWrapper from '../Pages/LandingPage/LandingPageWrapper';
import HomePage from '../Pages/LandingPage/HomePage';
import ContactUs from '../Pages/Contact Us/ContactUs';
import IULProfilesOutputWrapper from '../Pages/Profile/IULProfilesOutputWrapper';
import SAndPComparisonTab from '../Pages/Profile/IULProfileTabs/SAndPComparisonTab/SAndPComparisonTab';
import AccValueTab from '../Pages/Profile/IULProfileTabs/AccValueTab/AccValueTab';
import RetirementIncomeTab from '../Pages/Profile/IULProfileTabs/RetirementIncomeTab/RetirementIncomeTab';
import TaxesTab from '../Pages/Profile/IULProfileTabs/TaxesTab/TaxesTab';
import FeesTab from '../Pages/Profile/IULProfileTabs/FeesTab/FeesTab';
import ProfileNewAnnuityIncome from '../Pages/Profile/AnnuityProfiles/ProfileNewAnnuityIncome/ProfileNewAnnuityIncome';
import ProfileNewIUL from '../Pages/Profile/IULProfiles/ProfileNewIUL/ProfileNewIUL';
import ProfileNewAnnuityRothConversion from '../Pages/Profile/AnnuityProfiles/ProfileNewAnnuityRothConversion/ProfileNewAnnuityRothConversion';
import AdminDashboard from '../Pages/Dashboard/AdminDashboard';
import AnnuityIncomeOutput from '../Pages/Profile/AnnuityProfiles/ProfileNewAnnuityIncome/AnnuityIncomeOutput';
import AnnuityProfilesOutputWrapper from '../Pages/Profile/AnnuityProfilesOutputWrapper';
import ProfileNewAnnuityAccumulation from '../Pages/Profile/AnnuityProfiles/ProfileNewAnnuityAccumulation/ProfileNewAnnuityAccumulation';
import { isLandingHost } from '../constants/brand';
import UserAgreement from '../Pages/FooterPages/UserAgreement';
import PrivacyPolicy from '../Pages/FooterPages/PrivacyPolicy';
import TermsAndConditions from '../Pages/FooterPages/TermsAndConditions';
import DisclaimerPage from '../Pages/FooterPages/DisclaimerPage';
import NewUserRegistration from '../Pages/NewUserRegistration.jsx/NewUserRegistration';
import MyAccount from '../Pages/MyAccount/MyAccount';
import ChangePassword from '../Pages/ChangePassword/ChangePassword';
import ResetPassword from '../Pages/ResetPassword/ResetPassword';
import AnnuityAccumulationOutput from '../Pages/Profile/AnnuityProfiles/ProfileNewAnnuityAccumulation/AnnuityAccumulationOutput';
import ProfileRothConversionIndex from '../Pages/Profile/AnnuityProfiles/ProfileNewAnnuityRothConversion/ProfileRothConversionIndex';
import ClientSharingPage from '../Pages/ClientSharingPage/ClientSharingPage';
import InflationCalc from '../Pages/InflationCalculator/InflationCalculator';
import rbLogoFull from "../assets/Images/mar-lockup.svg";
import rbLogoCollapsed from "../assets/Images/mar-mark-white.svg";
import SpendDownCalc from '../Pages/SpendDownCalc/SpendDownCalc';
import RothQuoter from '../Pages/Quoter/RothQuoter';
import ProfileList from '../Pages/Profile/ProfileList/ProfileList';
import RegistrationSuccess from '../Pages/RegistrationSuccess/RegistrationSuccess';
import VerifyUserV2 from '../Pages/VerifyUser/VerifyUserV2';
import PendingRequestsWrapper from '../Pages/PendingRequests/PendingRequestsWrapper';
import SetNewPassword from '../Pages/SetNewPassword/SetNewPassword';

// Resolves the landing route for a given role. Replaces nested ternaries
// previously used inline in the SSO flow and the wildcard <Navigate>.
const getRoleBasedRoute = (role?: string): string => {
  if (role === 'admin') return '/dashboard';
  if (role === 'agent') return '/profile';
  return '/super-admin-dashboard';
};

// Guards a route behind a URL token param. Redirects to "/" if the token is missing.
const TokenRoute = ({
  children,
  tokenParam = 'token',
}: {
  children: any;
  tokenParam?: string;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get(tokenParam);
    if (!token) {
      navigate('/', { replace: true });
      return;
    }
    setIsValid(true);
    setIsValidating(false);
  }, [location, navigate, tokenParam]);

  if (isValidating) return <GlobalLoader />;
  return isValid ? children : <Navigate to="/" replace />;
};

const ApplicationRoutes = () => {
  const {
    flagLoader,
    UserMe,
    userDetail,
    setUserDetail,
    setFlagLoader,
    getCompanyLogoAPI,
    setCompanyDetails,
    verifySSOtokenAPI,
  } = useContext(UserContext) as UserContextPayload;

  const [isInitializing, setIsInitializing] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const publicPaths = new Set([
    '/', '/login', '/register', '/registration-success',
    '/privacy-policy', '/terms-and-conditions', '/user-agreement', '/disclaimer',
    '/resetpassword', '/verify_user', '/profile/calculators', '/contact-us',
    '/rothQuoter', '/password-setup',
  ]);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      const response = await getCompanyLogoAPI();
      if (response?.status && response?.data?.length > 0) {
        setCompanyDetails(response?.data[0]);
      } else {
        setCompanyDetails({
          company_id: 0,
          company_name: 'retirementbrain',
          company_description: 'retirementbrain',
          company_full_logo: rbLogoFull,
          company_collapse_logo: rbLogoCollapsed,
        });
      }
    };
    fetchCompanyDetails();
  }, []);

  useEffect(() => {
    const handleAuthError = () => {
      setUserDetail((prev) => ({ ...prev, flagLoggedIn: false }));
      if (!publicPaths.has(location.pathname)) navigate('/login', { replace: true });
    };

    // Returns true when navigation has been redirected to /verify_user
    // (caller should short-circuit further routing in that case).
    const applyUserDataAndCheckRegistered = (userData: any): boolean => {
      setUserDetail({ ...userData, flagLoggedIn: true });
      if (userData?.user_data?.user_status_name?.toLowerCase() === 'registered') {
        navigate('/verify_user');
        return true;
      }
      return false;
    };

    const handleSSOFlow = async (ssoToken: string) => {
      const ssoResponse = await verifySSOtokenAPI(ssoToken);
      if (!ssoResponse?.status) {
        navigate('/');
        return;
      }

      const userResponse = await UserMe();
      if (!userResponse?.status || !userResponse?.data) {
        navigate('/');
        return;
      }

      const userData = userResponse.data[0];
      if (applyUserDataAndCheckRegistered(userData)) return;

      const role = userData.user_data.user_role_type.toLowerCase();
      navigate(getRoleBasedRoute(role));
    };

    const normalAuthFlow = async () => {
      const response = await UserMe();
      if (response?.status && response?.data) {
        const userData = response.data[0];
        setUserDetail({ ...userData, flagLoggedIn: true });
        if (userData?.user_data?.user_status_name?.toLowerCase() === 'registered') {
          navigate('/verify_user');
        }
      } else {
        handleAuthError();
      }
    };

    const initializeAuth = async () => {
      try {
        setFlagLoader(true);
        const queryParams = new URLSearchParams(globalThis.location.search);
        const ssoToken = queryParams.get('sso_token');

        if (ssoToken) {
          await handleSSOFlow(ssoToken);
        } else {
          await normalAuthFlow();
        }
      } catch {
        handleAuthError();
      } finally {
        setFlagLoader(false);
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, []);

  if (isInitializing) return <GlobalLoader />;
  const roleType = userDetail?.user_data?.user_role_type?.toLowerCase();

  return (
    <>
      {(flagLoader || userDetail?.flagLoggedIn == null) && <GlobalLoader />}
      {isLandingHost(globalThis.location.host) ? (
        <Routes>
          <Route path="/" element={<LandingPageWrapper />}>
            <Route index element={<HomePage />} />
            <Route path="contact-us" element={<ContactUs />} />
          </Route>
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/user-agreement" element={<UserAgreement />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/profile/calculators" element={<TokenRoute><ClientSharingPage /></TokenRoute>} />
          <Route path="/rothQuoter" element={<RothQuoter />} />
          <Route path="/registration-success" element={<RegistrationSuccess />} />
          <Route
            path="/password-setup"
            element={
              <TokenRoute tokenParam="setup_token">
                <SetNewPassword />
              </TokenRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : (
        <Routes>
          {/* Truly public — no auth required, no token required */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/user-agreement" element={<UserAgreement />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/resetpassword" element={<ResetPassword />} />
          <Route path="/rothQuoter" element={<RothQuoter />} />
          <Route path="/profile/calculators" element={<TokenRoute><ClientSharingPage /></TokenRoute>} />
          <Route path="/registration-success" element={<RegistrationSuccess />} />

          {/* Password setup — token-gated but outside auth split
                        so the email link works whether the user is logged in or not */}
          <Route
            path="/password-setup"
            element={
              <TokenRoute tokenParam="setup_token">
                <SetNewPassword />
              </TokenRoute>
            }
          />

          {/* Auth-split routes */}
          {userDetail?.flagLoggedIn ? (
            <>
              <Route element={<MainComponent />}>
                <Route path="/dashboard" element={<AdminDashboard />} />
                <Route path="/agent" element={<Agent />} />
                <Route path="/profile" element={<Profile />}>
                  <Route index element={<ProfileList profileType="all" />} />
                  <Route path="allProfiles" element={<ProfileList profileType="all" />} />
                  <Route path="iulProfiles" element={<ProfileList profileType="iul" />} />
                  <Route path="annuityProfiles" element={<ProfileList profileType="annuity" />} />
                </Route>
                <Route path="/training" element={<Training />} />
                <Route path="/inflationcalculator" element={<InflationCalc />} />
                <Route path="/spendDownCalc" element={<SpendDownCalc />} />
                <Route path="/myAccount" element={<MyAccount />} />
                <Route path="/changePassword" element={<ChangePassword />} />
                <Route path="/pending-requests" element={<PendingRequestsWrapper />} />

                <Route path="/profile/iulProfileDetails" element={<IULProfilesOutputWrapper />}>
                  <Route index element={<SAndPComparisonTab />} />
                  <Route path="sp" element={<SAndPComparisonTab />} />
                  <Route path="accountValue" element={<AccValueTab />} />
                  <Route path="retirementIncome" element={<RetirementIncomeTab />} />
                  <Route path="taxes" element={<TaxesTab />} />
                  <Route path="fees" element={<FeesTab />} />
                </Route>

                <Route path="/profile/annuityProfileDetails" element={<AnnuityProfilesOutputWrapper />}>
                  <Route path="annuityRoth" element={<ProfileRothConversionIndex />} />
                  <Route path="annuityIncome" element={<AnnuityIncomeOutput />} />
                  <Route path="annuityAccumulation" element={<AnnuityAccumulationOutput />} />
                </Route>

                <Route path="/profile/newProfile/iul" element={<ProfileNewIUL />} />
                <Route path="/profile/newProfile/annuityIncome" element={<ProfileNewAnnuityIncome />} />
                <Route path="/profile/newProfile/annuityRothConversion" element={<ProfileNewAnnuityRothConversion />} />
                <Route path="/profile/newProfile/annuityAccumulation" element={<ProfileNewAnnuityAccumulation />} />

                {roleType === 'super admin' && (
                  <Route path="/super-admin-dashboard" element={<SuperAdminDashboard />} />
                )}
              </Route>

              <Route
                path="*"
                element={<Navigate to={getRoleBasedRoute(roleType)} replace />}
              />
            </>
          ) : (
            <>
              <Route path="/" element={<LandingPageWrapper />}>
                <Route index element={<HomePage />} />
                <Route path="contact-us" element={<ContactUs />} />
              </Route>
              <Route path="/login" element={<Signin />} />
              <Route path="/register" element={<NewUserRegistration />} />
              <Route path="/verify_user" element={<VerifyUserV2 />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      )}
    </>
  );
};

export default ApplicationRoutes;